import { Graphics } from 'pixi.js';
import { lerp } from '../../shared/scalar';
import type { SimState } from '../../sim/state';
import type { RenderContext } from '../context';
import type { Frame } from '../frame';
import { TAU } from '../paint';
import {
  AURA,
  COOL_DOTS,
  SMOKES,
  coolDot,
  heatRing,
  smoke,
  wisp,
  wobblyPoint,
} from './canicule-moments';

export interface Sun {
  readonly x: number;
  readonly y: number;
  readonly reach: number;
  readonly strength: number;
}

export interface SunFloor {
  update(state: SimState, alpha: number, frame: Frame, suns: readonly Sun[]): void;
  destroy(): void;
}

const HEAT = { ring: 0xffd6aa, ringWidth: 1.4, wisp: 0xffd7b0, wispWidth: 1.2, steps: 36 };
const SHADE = { fill: 0x287896, alpha: 0.3, rim: 0x7cf2b0, rimAlpha: 0.5, dashes: 24 };
const MIST = { fill: 0x3fd0c9, alpha: 0.1, dot: 0xbff7ff };
const SMOKE = { color: 0xffb08a, width: 1.4 };

export function createSunFloor(ctx: RenderContext): SunFloor {
  const { layers, shadeRadii, trapLooks } = ctx;
  const heat = layers.dim.addChild(new Graphics());
  const shade = layers.zones.addChild(new Graphics());
  const smokes = layers.fx.addChild(new Graphics());

  function aura(sun: Sun, beats: number, calm: boolean): void {
    if (!calm) {
      for (let index = 0; index < AURA.rings; index += 1) {
        const ring = heatRing(index, beats);
        const radius = ring.share * sun.reach;
        for (let step = 0; step <= HEAT.steps; step += 1) {
          const [dx, dy] = wobblyPoint(step, HEAT.steps, radius, ring.wobble, ring.phase);
          if (step === 0) {
            heat.moveTo(sun.x + dx, sun.y + dy);
          } else {
            heat.lineTo(sun.x + dx, sun.y + dy);
          }
        }
        heat.stroke({ width: HEAT.ringWidth, color: HEAT.ring, alpha: ring.alpha * sun.strength });
      }
    }
    for (let index = 0; index < AURA.wisps; index += 1) {
      const rising = wisp(index, beats);
      const x = sun.x + rising.x * sun.reach;
      const y = sun.y + rising.y * sun.reach;
      for (let step = 0; step < 6; step += 1) {
        const px = x + Math.sin(step * 1.4 + rising.sway) * 2;
        const py = y - step * 2.6;
        if (step === 0) {
          heat.moveTo(px, py);
        } else {
          heat.lineTo(px, py);
        }
      }
      heat.stroke({ width: HEAT.wispWidth, color: HEAT.wisp, alpha: rising.alpha * sun.strength });
    }
  }

  function parasol(x: number, y: number, radius: number): void {
    shade.circle(x, y, radius).fill({ color: SHADE.fill, alpha: SHADE.alpha });
    for (let index = 0; index < SHADE.dashes; index += 1) {
      const from = (index / SHADE.dashes) * TAU;
      const to = from + TAU / SHADE.dashes / 2;
      shade
        .moveTo(x + Math.cos(from) * radius, y + Math.sin(from) * radius)
        .arc(x, y, radius, from, to)
        .stroke({ width: 1.4, color: SHADE.rim, alpha: SHADE.rimAlpha });
    }
  }

  function mist(x: number, y: number, radius: number, beats: number): void {
    shade.circle(x, y, radius).fill({ color: MIST.fill, alpha: MIST.alpha });
    for (let index = 0; index < COOL_DOTS; index += 1) {
      const drop = coolDot(index, beats);
      shade
        .circle(x + drop.x * radius, y + drop.y * radius, 1.2)
        .fill({ color: MIST.dot, alpha: drop.alpha });
    }
  }

  function shades(state: SimState, alpha: number, beats: number): void {
    for (const player of state.players) {
      const radius = shadeRadii.get(player.classId);
      if (radius !== undefined && !player.downed) {
        parasol(lerp(player.prevX, player.x, alpha), lerp(player.prevY, player.y, alpha), radius);
      }
    }
    for (const trap of state.traps) {
      const radius = trapLooks.get(trap.kind)?.shade;
      if (radius !== undefined) {
        mist(lerp(trap.prevX, trap.x, alpha), lerp(trap.prevY, trap.y, alpha), radius, beats);
      }
    }
  }

  function smoking(state: SimState, alpha: number, beats: number): void {
    for (const player of state.players) {
      if ((player.sunTicks ?? 0) <= 0) {
        continue;
      }
      const x = lerp(player.prevX, player.x, alpha);
      const y = lerp(player.prevY, player.y, alpha);
      for (let index = 0; index < SMOKES; index += 1) {
        const puff = smoke(index, beats);
        for (let step = 0; step < 5; step += 1) {
          const px = x + (puff.x + Math.sin(step * 1.5 + puff.sway) * 0.15) * player.radius;
          const py = y + (puff.y - step * 0.2) * player.radius;
          if (step === 0) {
            smokes.moveTo(px, py);
          } else {
            smokes.lineTo(px, py);
          }
        }
        smokes.stroke({ width: SMOKE.width, color: SMOKE.color, alpha: puff.alpha, cap: 'round' });
      }
    }
  }

  return {
    update(state, alpha, frame, suns): void {
      heat.clear();
      shade.clear();
      smokes.clear();
      const beats = frame.now / frame.tempo.ticksPerBeat;
      for (const sun of suns) {
        aura(sun, beats, frame.calm);
      }
      if (suns.length > 0) {
        shades(state, alpha, beats);
      }
      smoking(state, alpha, beats);
    },
    destroy(): void {
      for (const graphics of [heat, shade, smokes]) {
        graphics.destroy();
      }
    },
  };
}
